import type { Container } from 'pixi.js';
import { GAME } from '@/config/game';
import { shakeScale } from '@/config/fx';

/**
 * Follow camera: approaches the target via lerp, supports shake,
 * positions the world container with interpolation at render time.
 * Clamped to the room's bounds (never shows empty space at the edges).
 */
export class Camera {
  private x = 0;
  private y = 0;
  private prevX = 0;
  private prevY = 0;
  private targetX = 0;
  private targetY = 0;
  private shakeAmp = 0;
  private time = 0;

  /** Clamp bounds (room bounding box; routes may start below 0) — 0 size = no clamping */
  boundsX = 0;
  boundsY = 0;
  boundsW = 0;
  boundsH = 0;
  /** Last view size seen by update() — culling + the objective arrow */
  viewW = 0;
  viewH = 0;

  /** Camera centre (logic position) in world space. */
  get centerX(): number {
    return this.x;
  }

  get centerY(): number {
    return this.y;
  }

  /** Clamp the view to a world rect. */
  setBounds(x: number, y: number, w: number, h: number): void {
    this.boundsX = x;
    this.boundsY = y;
    this.boundsW = w;
    this.boundsH = h;
  }

  snapTo(x: number, y: number): void {
    this.x = this.prevX = this.targetX = x;
    this.y = this.prevY = this.targetY = y;
  }

  follow(x: number, y: number): void {
    this.targetX = x;
    this.targetY = y;
  }

  /** Request a shake; scaled by the screen-shake setting (OFF / LOW / FULL). */
  shake(amount: number): void {
    const scaled = amount * shakeScale();
    if (scaled <= 0) return;
    this.shakeAmp = Math.max(this.shakeAmp, scaled);
  }

  /** Called on the fixed timestep */
  update(dt: number, screenW: number, screenH: number): void {
    this.prevX = this.x;
    this.prevY = this.y;
    this.time += dt;
    this.viewW = screenW;
    this.viewH = screenH;

    this.x += (this.targetX - this.x) * GAME.camera.lerp;
    this.y += (this.targetY - this.y) * GAME.camera.lerp;

    if (this.boundsW > 0) {
      const hw = screenW / 2;
      const hh = screenH / 2;
      const bx = this.boundsX;
      const by = this.boundsY;
      // Center if the screen is larger than the bounds, otherwise clamp
      this.x = this.boundsW < screenW ? bx + this.boundsW / 2 : clamp(this.x, bx + hw, bx + this.boundsW - hw);
      this.y = this.boundsH < screenH ? by + this.boundsH / 2 : clamp(this.y, by + hh, by + this.boundsH - hh);
    }

    this.shakeAmp = Math.max(0, this.shakeAmp - GAME.camera.shakeDecay * dt * this.shakeAmp - 0.5 * dt);
  }

  /** Called at render time — positions the world container */
  apply(world: Container, screenW: number, screenH: number, alpha: number): void {
    const cx = this.prevX + (this.x - this.prevX) * alpha;
    const cy = this.prevY + (this.y - this.prevY) * alpha;

    // Deterministic shake (sine mix — no rng needed)
    const t = this.time * 60;
    const sx = this.shakeAmp * (Math.sin(t * 1.3) * 0.6 + Math.sin(t * 3.7) * 0.4);
    const sy = this.shakeAmp * (Math.cos(t * 1.7) * 0.6 + Math.cos(t * 2.9) * 0.4);

    world.position.set(screenW / 2 - cx + sx, screenH / 2 - cy + sy);
  }

  /** World coordinate → screen coordinate (for aim calculation) */
  worldToScreen(wx: number, wy: number, screenW: number, screenH: number): { x: number; y: number } {
    return { x: screenW / 2 - this.x + wx, y: screenH / 2 - this.y + wy };
  }
}

function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}
